import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCreateCoach, getListCoachesQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrg } from "@/lib/orgContext";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { useAuth } from "@clerk/react";
import { CustomFieldsSection, saveCustomFieldValues } from "@/components/CustomFieldsSection";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { Link } from "wouter";
import { NCAA_SPORTS } from "@/lib/sports";
import { COACH_POSITIONS } from "@/lib/coachPositions";

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  sport: z.string().min(1, "Sport is required"),
  title: z.string().min(1, "Title is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().optional(),
  hireDate: z.string().min(1, "Hire date is required"),
  status: z.enum(["active", "inactive", "on_leave"]),
  programGender: z.enum(["mens", "womens", "coed"]).optional(),
});

export default function NewCoach() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createCoach = useCreateCoach();
  const { org } = useOrg();
  const { getToken } = useAuth();
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (org && org.role !== "admin") {
      toast({ title: "Access denied", description: "Viewers cannot create coach profiles.", variant: "destructive" });
      setLocation("/coaches");
    }
  }, [org, setLocation, toast]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      sport: "",
      title: "",
      email: "",
      phone: "",
      hireDate: "",
      status: "active",
      programGender: undefined,
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    createCoach.mutate({ data: values }, {
      onSuccess: async (coach) => {
        const hasCustomFields = Object.keys(customFieldValues).length > 0;
        if (hasCustomFields) {
          const token = await getToken();
          if (token) {
            await saveCustomFieldValues("coach", coach.id, customFieldValues, token);
          }
        }
        toast({ title: "Coach Profile Created", description: "The coach profile has been successfully created." });
        queryClient.invalidateQueries({ queryKey: getListCoachesQueryKey() });
        setLocation(`/coaches/${coach.id}`);
      },
      onError: (err: unknown) => {
        // ApiError exposes `.status` and `.data` (parsed JSON body) directly.
        const e = err as { status?: number; data?: { error?: string; message?: string } };
        const status = e.status;
        const data = e.data;
        if (status === 403 && data?.error === "plan_limit_reached") {
          toast({
            title: "Plan limit reached",
            description: data.message ?? "Upgrade to Pro for unlimited coaches.",
            variant: "destructive",
          });
          setLocation("/settings#plans");
          return;
        }
        handleApiError(toast, err, { fallbackTitle: "Could not create coach profile" });
      },
    });
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/coaches"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">New Coach Profile</h1>
          <p className="text-muted-foreground mt-1">Add a new coach to the system.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Personal & Role Details</CardTitle>
          <CardDescription>Enter the coach's contact information and assignment.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="firstName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl><Input placeholder="Jane" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="lastName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl><Input placeholder="Smith" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="sport" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sport *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Select a sport" /></SelectTrigger>
                      </FormControl>
                      <SelectContent className="max-h-[300px]">
                        {NCAA_SPORTS.map((sport) => (
                          <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ""}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Select title" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {COACH_POSITIONS.map((p) => (
                          <SelectItem key={p.value} value={p.label}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Institutional Email *</FormLabel>
                    <FormControl><Input type="email" placeholder="coach@university.edu" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="phone" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone (optional)</FormLabel>
                    <FormControl><Input placeholder="(555) 123-4567" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="hireDate" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hire Date *</FormLabel>
                    <FormControl><Input type="date" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="status" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="on_leave">On Leave</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="programGender" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Program Gender</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v === "_unset" ? undefined : v)}
                      value={field.value ?? "_unset"}
                    >
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="_unset">Unassigned</SelectItem>
                        <SelectItem value="mens">Men's</SelectItem>
                        <SelectItem value="womens">Women's</SelectItem>
                        <SelectItem value="coed">Co-ed</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>

              <div className="flex justify-end gap-4">
                <Button variant="outline" type="button" onClick={() => setLocation("/coaches")}>Cancel</Button>
                <Button type="submit" disabled={createCoach.isPending}>
                  {createCoach.isPending ? "Creating..." : "Create Profile"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <CustomFieldsSection
        entityType="coach"
        values={customFieldValues}
        onChange={(key, value) => setCustomFieldValues((prev) => ({ ...prev, [key]: value }))}
      />
    </div>
  );
}
